import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogIn, Users } from "lucide-react";
import { toast } from "sonner";
import { getStudentId } from "@/lib/api";
import { getSessionInfo, joinStudySession, setActiveSessionCode } from "@/lib/studyApi";

export default function JoinStudySession() {
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [joining, setJoining] = useState(false);

  const lookup = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setLoading(true);
    setInfo(null);
    try {
      const { data } = await getSessionInfo(code.trim().toUpperCase());
      if (data.participant_count >= data.max_participants) {
        toast.error("This Study Session is full. A maximum of 3 students can participate.");
        return;
      }
      setInfo(data);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 410) toast.error("This Session ID has expired. Please ask the host to create a new session.");
      else toast.error("Session not found. Please check the ID and try again.");
    } finally {
      setLoading(false);
    }
  };

  const join = async () => {
    setJoining(true);
    try {
      const studentId = getStudentId();
      await joinStudySession(code.trim().toUpperCase(), studentId);
      setActiveSessionCode(code.trim().toUpperCase());
      navigate(`/study/lobby/${code.trim().toUpperCase()}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't join the session.");
    } finally {
      setJoining(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4" data-testid="join-study-session-page">
      <div className="max-w-md w-full">
        <div className="flex items-center gap-2 mb-6 justify-center">
          <Users className="text-primary" size={22} />
          <h1 className="font-heading font-extrabold text-2xl">Join a Session</h1>
        </div>
        {!info ? (
          <form onSubmit={lookup} className="bg-card border border-border rounded-3xl shadow-lg p-6 space-y-4">
            <label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Enter Session ID</label>
            <input
              data-testid="join-session-code-input"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="SG-XXXXXX"
              className="w-full h-12 rounded-xl border border-border bg-background px-4 text-center font-heading font-bold tracking-wider text-lg focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
            <button type="submit" disabled={loading} data-testid="join-session-lookup-button"
              className="w-full h-12 rounded-xl bg-primary text-primary-foreground font-bold flex items-center justify-center gap-2 hover:bg-primary/90 active:scale-95 transition-all disabled:opacity-60">
              {loading ? "Checking..." : "Find Session"}
            </button>
          </form>
        ) : (
          <div className="bg-card border border-border rounded-3xl shadow-lg p-6 space-y-4" data-testid="join-session-confirmation">
            <p className="font-heading font-bold text-lg">You're joining a SmartGuru Study Session</p>
            <div className="space-y-1.5 text-sm">
              <p><span className="text-muted-foreground">Subject:</span> <span className="font-semibold">{info.subject}</span></p>
              <p><span className="text-muted-foreground">Grade:</span> <span className="font-semibold">{info.standard}</span></p>
              <p><span className="text-muted-foreground">Topic:</span> <span className="font-semibold">{info.topic}</span></p>
              <p><span className="text-muted-foreground">Host:</span> <span className="font-semibold">{info.host_display_name}</span></p>
              <p><span className="text-muted-foreground">Participants:</span> <span className="font-semibold">{info.participant_count} / {info.max_participants}</span></p>
            </div>
            <button onClick={join} disabled={joining} data-testid="join-session-confirm-button"
              className="w-full h-12 rounded-xl bg-primary text-primary-foreground font-bold flex items-center justify-center gap-2 hover:bg-primary/90 active:scale-95 transition-all disabled:opacity-60">
              <LogIn size={16} /> {joining ? "Joining..." : "Join Session"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
